import React from 'react';

import LanguageToggle from './LanguageToggle';
import { Scene, SceneIcon } from '../icons';
import { Language, LanguageId } from '../i18n';
import { WeatherDataRecord } from '../types';
import {
    apparentTemperature,
    fahToCelsius,
    formatNumber,
    formatTime,
    hasValue,
    mphToMs,
    type MaybeNumber
} from '../utils';

// Data older than this is flagged as stale in the status pill.
const STALE_AFTER_MS = 10 * 60 * 1000;

// Accent colour for the big temperature, from icy blue to hot red.
function temperatureAccent(tempC: MaybeNumber) {
    if (!hasValue(tempC)) return '#a5d8ff';
    if (tempC < 0) return '#a5d8ff';
    if (tempC < 10) return '#7dd3fc';
    if (tempC < 18) return '#86efac';
    if (tempC < 26) return '#fde68a';
    if (tempC < 32) return '#fdba74';
    return '#fca5a5';
}

interface HeroProps {
    data: WeatherDataRecord | undefined; // undefined while loading: skeletons instead of values
    offline: boolean; // the server stopped answering: says so in the status pill, keeps the last values
    now: number;
    scene: Scene;
    t: Language;
    language: LanguageId;
    onLanguageChange: () => void;
}

// Top of the page: status pill, language toggle, location, date, temperature and scene icon, feels like.
const Hero: React.FC<HeroProps> = ({ data, offline, now, scene, t, language, onLanguageChange }) => {
    const dateStr = new Date(now).toLocaleDateString(t.locale, { weekday: 'long', day: 'numeric', month: 'long' });

    const topbar = (status: React.ReactNode, statusClass = 'status') => (
        <div className="topbar">
            <div className={statusClass}>{status}</div>
            <LanguageToggle language={language} label={t.switchLanguage} onLanguageChange={onLanguageChange} />
        </div>
    );

    if (data === undefined) {
        return (
            <header className="hero">
                {topbar(<><span className="pulse" />{offline ? t.offline : t.loading}</>, offline ? 'status offline' : 'status')}
                <h1 className="location">{t.location}</h1>
                <p className="date">{dateStr}</p>
                <div className="now">
                    <div className="skeleton skeleton-temp" />
                    <SceneIcon scene={scene} />
                </div>
                <div className="skeleton skeleton-line" />
            </header>
        );
    }

    const updated = data.dateUtc ? new Date(data.dateUtc).getTime() : NaN;
    const ageMs = isNaN(updated) ? NaN : Math.max(0, now - updated);
    const ageMin = Math.floor(ageMs / 60000);
    const agoStr = isNaN(ageMs) ? ''
        : ageMin < 1 ? t.justNow
        : ageMin < 60 ? t.minutesAgo(ageMin)
        : t.hoursAgo(Math.floor(ageMin / 60));
    const stale = isNaN(ageMs) || ageMs > STALE_AFTER_MS;
    const hasReading = !isNaN(updated); // false for the server's empty record before the first upload

    const tempC = fahToCelsius(data.tempF);
    const tempStr = formatNumber(tempC, 1);
    const feels = apparentTemperature(tempC, data.humidity, mphToMs(data.windSpeedMph));

    return (
        <header className="hero">
            {topbar(
                <>
                    <span className="pulse" />
                    {offline && !hasReading ? <span>{t.offline}</span>
                        : <span>{hasReading ? formatTime(data.dateUtc, t.locale, t.hour12) : t.noData}</span>}
                    {offline && hasReading ? <span className="status-ago">· {t.offline}</span>
                        : agoStr && <span className="status-ago">· {agoStr}</span>}
                </>,
                `status ${offline ? 'offline' : stale ? 'stale' : ''}`)}

            <h1 className="location">{t.location}</h1>
            <p className="date">{dateStr}</p>

            <div className="now">
                <div className="temp" style={{ '--accent': temperatureAccent(tempC) } as React.CSSProperties}>
                    <span key={tempStr} className="tick">{tempStr}</span>
                    <span className="deg">°</span>
                </div>
                <SceneIcon scene={scene} />
            </div>

            <p className="feels">
                {t.feelsLike} <b>{formatNumber(feels, 0)}°</b>
                <span className="dot">•</span>
                {t.dewPoint} <b>{formatNumber(fahToCelsius(data.dewPtF), 0)}°</b>
            </p>
        </header>
    );
};

export default Hero;
